// React
import React, { createContext, useState, useCallback, useContext } from "react";

// third party libraries
import { Modal } from "react-bootstrap";

// kits
import { CheckInput, CustomInput } from "../kits/Inputs/Inputs";
import CustomButton from "../kits/button/CustomButton";
import useAxios from "../../services/useAxios";
import { InfoCircleIcon } from "../kits/Icons";

// Other Components
import TimePicker from "../singleProduct/TimePicker";

// Helpers
import { NodeType, ResponseType, UpdateStepRequestType } from "./SiCaTypes";
import { apiResponseHandler } from "./SiCahelper";
import { CustomersFields, UsersFields } from "./SiCaData";
import { inputLabel } from "./SiCaStyles";

// Components

// Contexts
import CampaignContext from "./CampaignContext";

interface ModalContextType {
  openModal: (data: NodeType) => void;
}

const ModalContext = createContext<ModalContextType>({
  openModal: (data) => {},
});

enum ModalsShowType {
  Closed,
  EditModalOpened,
  DynamicTextModalOpened,
}

interface ModalContextProviderProps {
  children?: JSX.Element | JSX.Element[];
}

const ModalContextProvider: React.FC<ModalContextProviderProps> = ({
  children,
}) => {
  const { nodes, edges, changeNodes, changeEdges } =
    useContext(CampaignContext);

  const { execute: UpdateStepAPI } = useAxios("api/Update_Step");

  const [show, setShow] = useState<ModalsShowType>(ModalsShowType.Closed);

  const [inputs, setInputs] = useState<NodeType>({
    id: 0,
    title: "",
    description: "",
    time: 0,
    automate: false,
    text: "",
    isActive: true,
    type: 0,
    trueLabel: "",
    falseLabel: "",
  });

  const openModal = useCallback((data: NodeType) => {
    setInputs(data);
    setShow(ModalsShowType.EditModalOpened);
  }, []);

  const onSubmitHandler = useCallback<React.FormEventHandler<HTMLFormElement>>(
    (event) => {
      event.preventDefault();

      const data: UpdateStepRequestType = {
        conditions: `id/=/${inputs.id}`,
        keyvalues: `title,${inputs.title}@@description,${
          inputs.description
        }@@time,${inputs.time}@@automate,${inputs.automate ? 1 : 0}@@status,${
          inputs.isActive ? 2 : 1
        }@@text,${inputs.text}@@false_label,${inputs.falseLabel}@@true_label,${
          inputs.trueLabel
        }`,
      };

      UpdateStepAPI(data).then((res: ResponseType) => {
        apiResponseHandler(res, {
          success: "گره با موفقیت ویرایش شد.",
          failure: "مشکلی در ویرایش گره بوجود آمده است، لطفا مجددا تلاش کنید.",
        });
        if (res.status) {
          // changing node data
          changeNodes(
            nodes.map((node) => {
              if (node.data.id === inputs.id) {
                node.data = inputs;
              }
              return node;
            })
          );

          // changing edge labels according to node's label's changes
          changeEdges(
            edges.map((edge) => {
              if (+edge.source === inputs.id) {
                if (edge.sourceHandle === "true") {
                  edge.label = inputs.trueLabel;
                } else if (edge.sourceHandle === "false") {
                  edge.label = inputs.falseLabel;
                }
              }
              return edge;
            })
          );

          // close modal
          setShow(ModalsShowType.Closed);
        }
      });
    },
    [inputs, nodes, edges, changeNodes, changeEdges]
  );

  const titleOnChangeHandler = useCallback<
    React.ChangeEventHandler<HTMLInputElement>
  >(
    (event) => {
      setInputs((prev) => ({
        ...prev,
        title: event.target.value,
      }));
    },
    [setInputs]
  );
  const descriptionOnChangeHandler = useCallback<
    React.ChangeEventHandler<HTMLInputElement>
  >(
    (event) => {
      setInputs((prev) => ({
        ...prev,
        description: event.target.value,
      }));
    },
    [setInputs]
  );
  const timeOnChangeHandler = useCallback(
    (time: number) => {
      setInputs((prev) => ({
        ...prev,
        time: +time,
      }));
    },
    [setInputs]
  );
  const automateOnChangeHandler = useCallback<
    React.ChangeEventHandler<HTMLInputElement>
  >(
    (event) => {
      setInputs((prev) => ({
        ...prev,
        automate: event.target.checked,
      }));
    },
    [setInputs]
  );
  const isActiveOnChangeHandler = useCallback<
    React.ChangeEventHandler<HTMLInputElement>
  >(
    (event) => {
      setInputs((prev) => ({
        ...prev,
        isActive: event.target.checked,
      }));
    },
    [setInputs]
  );
  const textOnChangeHandler = useCallback<
    React.ChangeEventHandler<HTMLInputElement>
  >(
    (event) => {
      setInputs((prev) => ({
        ...prev,
        text: event.target.value,
      }));
    },
    [setInputs]
  );
  const trueLabelOnChangeHandler = useCallback<
    React.ChangeEventHandler<HTMLInputElement>
  >(
    (event) => {
      setInputs((prev) => ({
        ...prev,
        trueLabel: event.target.value,
      }));
    },
    [setInputs]
  );
  const falseLabelOnChangeHandler = useCallback<
    React.ChangeEventHandler<HTMLInputElement>
  >(
    (event) => {
      setInputs((prev) => ({
        ...prev,
        falseLabel: event.target.value,
      }));
    },
    [setInputs]
  );

  const value: ModalContextType = { openModal };
  return (
    <ModalContext.Provider {...{ value }}>
      {children}
      <Modal
        show={show === ModalsShowType.EditModalOpened}
        onHide={() => setShow(ModalsShowType.Closed)}
      >
        <Modal.Header closeButton>
          <Modal.Title>ویرایش عملیات</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <form onSubmit={onSubmitHandler}>
            <CustomInput
              inputContainer="mb-3"
              label={"عنوان"}
              type="text"
              inputValue={inputs.title}
              // @ts-ignore
              onChangeFunc={titleOnChangeHandler}
            />
            <CustomInput
              inputContainer="mb-3"
              label={"توضیحات"}
              type="textarea"
              inputValue={inputs.description}
              // @ts-ignore
              onChangeFunc={descriptionOnChangeHandler}
            />
            <div className="mb-3">
              <label className="mb-2" style={inputLabel}>
                زمان تاخیر اجرای گره
              </label>
              <TimePicker
                func={timeOnChangeHandler}
                defaultTime={inputs.time}
              />
            </div>
            <CustomInput
              inputContainer="mb-3"
              // @ts-ignore
              label={
                <>
                  <p>متن</p>
                  <span
                    onClick={() =>
                      setShow(ModalsShowType.DynamicTextModalOpened)
                    }
                  >
                    <InfoCircleIcon width={20} height={20} />
                  </span>
                </>
              }
              labelClassName="d-flex justify-content-between"
              type="textarea"
              inputValue={inputs.text}
              // @ts-ignore
              onChangeFunc={textOnChangeHandler}
            />
            <CustomInput
              inputContainer="mb-3"
              label={"متن در صورت موفق بودن"}
              placeholder="موفق"
              inputValue={inputs.trueLabel}
              // @ts-ignore
              onChangeFunc={trueLabelOnChangeHandler}
            />
            <CustomInput
              inputContainer="mb-3"
              label={"متن در صورت ناموفق بودن"}
              placeholder="ناموفق"
              inputValue={inputs.falseLabel}
              // @ts-ignore
              onChangeFunc={falseLabelOnChangeHandler}
            />
            <div className="row">
              <CheckInput
                inputContainer="mb-3 col"
                label={"اتوماتیک است"}
                type="checkbox"
                inputValue={inputs.automate}
                // @ts-ignore
                onChangeFunc={automateOnChangeHandler}
              />
              <CheckInput
                inputContainer="mb-3 col"
                label={"فعال است"}
                type="checkbox"
                inputValue={inputs.isActive}
                // @ts-ignore
                onChangeFunc={isActiveOnChangeHandler}
              />
            </div>

            <div className="d-flex justify-content-center">
              <CustomButton
                className=""
                color="success"
                btnType="submit"
                onClick={() => {}}
              >
                ثبت تغییرات
              </CustomButton>
            </div>
          </form>
        </Modal.Body>
      </Modal>
      <Modal
        show={show === ModalsShowType.DynamicTextModalOpened}
        onHide={() => setShow(ModalsShowType.EditModalOpened)}
      >
        <Modal.Header closeButton>
          <Modal.Title>راهنمای متن</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p className="mb-4">
            برای جدا سازی متن متغییر از متن ثابت از ** استفاده کنید.
            <br />
            برای مثال:
            <br />
            <br />
            <span style={{ fontSize: "0.9rem" }}>
              مشتری گرامی، جناب **customer__firstname** **customer__lastname**،
              پیشنهادات ویژه ما را از دست ندهید.
            </span>
          </p>
          <div>
            <h6 className="mb-2">لیست متغییر‌هایی که می‌توان استفاده کرد:</h6>
            <div className="row row-cols-1 row-cols-md-2">
              {CustomersFields.map((v, i) => (
                <p key={`customers-${i}`}>customers__{v}</p>
              ))}
              {UsersFields.map((v, i) => (
                <p key={`users-${i}`}>users__{v}</p>
              ))}
            </div>
          </div>
        </Modal.Body>
      </Modal>
    </ModalContext.Provider>
  );
};

export default ModalContext;

export { ModalContextProvider };
